import React, { useState } from 'react';
import { Lock, Mail, AlertCircle, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { signIn, resetPassword } from '../../services/authService';
import { AdminUser } from '../../types';

interface AdminLoginFormProps {
  onLoginSuccess: (admin: AdminUser) => void;
  onBackToSite: () => void;
}

const AdminLoginForm: React.FC<AdminLoginFormProps> = ({ onLoginSuccess, onBackToSite }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const admin = await signIn(email, password);
      onLoginSuccess(admin);
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else {
        setError(err.message || 'Failed to send reset email.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    'w-full pl-10 pr-4 py-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="min-h-screen bg-forge-deep flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-paper-bright shadow-float overflow-hidden">
          {/* Header */}
          <div className="bg-forge text-paper px-6 py-7 text-center">
            <div className="w-14 h-14 bg-ember flex items-center justify-center mx-auto mb-5">
              <Lock className="w-6 h-6 text-paper-warm" strokeWidth={1.5} />
            </div>
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-3">
              — The Machete Man
            </p>
            <h1 className="font-editorial italic font-light text-3xl text-paper-warm leading-tight tracking-tight">
              Admin portal.
            </h1>
          </div>

          {/* Form */}
          {showForgotPassword ? (
            // Forgot Password Form
            <div className="p-6 lg:p-8 space-y-5">
              {resetSent ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-olive/15 rounded-full flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="w-6 h-6 text-olive" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-editorial italic text-xl text-ink-950 mb-2">Check your email.</h3>
                  <p className="text-ink-700 text-sm mb-6">
                    Password reset link sent to{' '}
                    <span className="text-ink-950 font-medium">{email}</span>.
                  </p>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetSent(false);
                      setError('');
                    }}
                    className="inline-flex items-center gap-2 text-sm text-ember hover:text-ember-deep transition-colors duration-150"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setError('');
                    }}
                    className="inline-flex items-center gap-1 text-ink-500 hover:text-ink-700 text-sm transition-colors duration-150"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Back to sign in
                  </button>

                  <div>
                    <h3 className="font-editorial italic text-xl text-ink-950 mb-1">Reset password.</h3>
                    <p className="text-ink-700 text-sm">Enter your email and we'll send you a reset link.</p>
                  </div>

                  {error && (
                    <div className="bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
                      <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <p className="text-ember-deep text-sm">{error}</p>
                    </div>
                  )}

                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={inputClass}
                        placeholder="admin@themacheteman.com"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        Sending...
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </button>
                </form>
              )}
            </div>
          ) : (
            // Login Form
            <form onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-5">
              {error && (
                <div className="bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-ember-deep text-sm">{error}</p>
                </div>
              )}

              <div>
                <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="admin@themacheteman.com"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                    placeholder="Enter password"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError('');
                  }}
                  className="text-sm text-ember hover:text-ember-deep transition-colors duration-150"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="px-6 lg:px-8 pb-6 lg:pb-8">
            <button
              onClick={onBackToSite}
              className="w-full text-center text-ink-500 text-sm hover:text-ember transition-colors duration-150"
            >
              Back to main site
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginForm;
